import { useEffect, useState } from "react";
import { Briefcase, Clock, MapPin, ArrowRight, X, Upload, Check } from "lucide-react";
import api from "../lib/api";
import Reveal from "../components/Reveal";
import { toast, Toaster } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "full-time", label: "Full-time" },
  { key: "part-time", label: "Part-time" },
  { key: "internship", label: "Internships" },
];

export default function Career() {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openJob, setOpenJob] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/jobs");
        setJobs(res.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = filter === "all" ? jobs : jobs.filter((j) => j.employment_type === filter);

  return (
    <div data-testid="career-page" className="pt-32 pb-20">
      <Toaster position="top-center" theme="dark" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">Careers</span></Reveal>
        <Reveal delay={0.1}><h1 className="mt-4 text-5xl sm:text-6xl font-bold">Build the future <span className="flame-text">with us</span></h1></Reveal>
        <Reveal delay={0.2}><p className="mt-6 max-w-2xl text-lg text-zinc-400">Join a team that ships software for 50+ clients across the world — including the Govt of WB. Full-time, part-time and internship openings.</p></Reveal>

        <div className="flex flex-wrap gap-2 mt-10" data-testid="career-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm border transition-all ${
                filter === f.key
                  ? "bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black border-transparent font-semibold"
                  : "border-white/15 text-zinc-300 hover:bg-white/5"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-10 space-y-4">
          {loading ? (
            <p className="text-zinc-500">Loading positions...</p>
          ) : filtered.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center text-zinc-400">
              No openings match this filter right now. Check back soon, or send your CV to <span className="text-[#FF6B1A]">contact@codeflarelabs.com</span>.
            </div>
          ) : (
            filtered.map((job, i) => (
              <Reveal key={job.id} delay={i * 0.05}>
                <div data-testid={`job-card-${job.id}`} className="glass rounded-2xl p-6 hover:border-[#FF6B1A]/40 transition-all flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FF6B1A]/10 border border-[#FF6B1A]/40 text-[#FFAA33]">{job.employment_type}</span>
                      {job.salary_range && <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">{job.salary_range}</span>}
                    </div>
                    <h3 className="text-xl font-bold">{job.title}</h3>
                    <p className="text-zinc-400 text-sm mt-1.5">{job.description}</p>
                    <div className="flex gap-4 mt-3 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> Posted {new Date(job.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenJob(job)}
                    data-testid={`apply-btn-${job.id}`}
                    className="px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold hover:shadow-[0_0_20px_rgba(255,107,26,0.5)] inline-flex items-center gap-2"
                  >
                    Apply <ArrowRight size={16} />
                  </button>
                </div>
              </Reveal>
            ))
          )}
        </div>
      </div>

      {openJob && <ApplyModal job={openJob} onClose={() => setOpenJob(null)} />}
    </div>
  );
}

function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [files, setFiles] = useState({ photo: null, resume: null, govt_id: null });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFile = (k) => (e) => setFiles((f) => ({ ...f, [k]: e.target.files?.[0] || null }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!files.photo || !files.resume || !files.govt_id) {
      toast.error("Please upload photo, resume and govt ID.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("photo", files.photo);
      fd.append("resume", files.resume);
      fd.append("govt_id", files.govt_id);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="apply-modal">
      <div className="relative w-full max-w-2xl my-8 rounded-3xl glass p-8">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10" data-testid="apply-modal-close">
          <X size={20} />
        </button>
        {done ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-5">
              <Check className="text-green-400" size={28} />
            </div>
            <h3 className="text-2xl font-bold mb-2">Application received</h3>
            <p className="text-zinc-400">We'll review your profile and get back via email.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold">Close</button>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-widest text-[#FF6B1A]">Applying for</p>
            <h2 className="text-2xl font-bold mt-1 mb-1">{job.title}</h2>
            <p className="text-sm text-zinc-400 mb-6">{job.employment_type} · {job.location}</p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full name" name="name" value={form.name} onChange={onChange} required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400">Cover letter (optional)</label>
                <textarea
                  name="cover_letter"
                  value={form.cover_letter}
                  onChange={onChange}
                  rows={3}
                  className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A]"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FileField label="Photo" file={files.photo} onChange={onFile("photo")} accept="image/*" required />
                <FileField label="Resume (PDF/DOC)" file={files.resume} onChange={onFile("resume")} accept=".pdf,.doc,.docx" required />
                <FileField label="Govt ID" file={files.govt_id} onChange={onFile("govt_id")} accept="image/*,.pdf" required />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="apply-submit-btn"
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-400">{label}</label>
      <input
        {...props}
        className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A]"
      />
    </div>
  );
}

function FileField({ label, file, onChange, accept, required }) {
  return (
    <label className="block cursor-pointer">
      <span className="text-xs uppercase tracking-widest text-zinc-400">{label}</span>
      <div className="mt-1.5 rounded-xl bg-white/5 border border-dashed border-white/15 px-4 py-3 text-sm flex items-center gap-2 hover:border-[#FF6B1A]/40">
        <Upload size={14} className="text-[#FF6B1A]" />
        <span className="truncate text-zinc-300">{file ? file.name : "Choose file..."}</span>
      </div>
      <input type="file" accept={accept} onChange={onChange} required={required} className="hidden" />
    </label>
  );
}
